import type { BuilderDraft, BuilderField, FormAnswer, FormAnswers } from "../types/formBuilderTypes";

export const MAX_SIGNATURE_BASE64_LENGTH = 1_500_000;

function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return year > 0 && month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1];
}

export function validateFormAnswer(field: BuilderField, answer: FormAnswer | undefined): string | undefined {
  const missing = answer === undefined || answer === ""
    || (field.widget === "multipleChoice" && Array.isArray(answer) && answer.length === 0);
  if (missing) return field.required ? "This question is required." : undefined;

  if (field.widget === "yesNo") return typeof answer === "boolean" ? undefined : "Choose Yes or No.";
  if (field.widget === "multipleChoice") {
    return Array.isArray(answer) && new Set(answer).size === answer.length
      && answer.every(value => field.choices.some(choice => choice.value === value))
      ? undefined : "Choose only the available options.";
  }
  if (typeof answer !== "string") return "Enter a text value.";
  switch (field.widget) {
    case "number":
      return /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(answer) && Number.isFinite(Number(answer))
        ? undefined : "Enter a valid, finite number.";
    case "email":
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answer) ? undefined : "Enter a valid email address.";
    case "date":
      return isCalendarDate(answer) ? undefined : "Enter a real date in YYYY-MM-DD format.";
    case "radio":
    case "dropdown":
      return field.choices.some(choice => choice.value === answer) ? undefined : "Choose an available option.";
    case "signature":
      return answer.length <= MAX_SIGNATURE_BASE64_LENGTH && answer.length % 4 === 0
        && answer.startsWith("iVBORw0KGgo") && /^[A-Za-z0-9+/]+={0,2}$/.test(answer)
        ? undefined : "Clear the signature and draw it again.";
    default:
      return undefined;
  }
}

export function validateFormAnswers(draft: BuilderDraft, answers: FormAnswers): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of Object.values(draft.fields)) {
    const error = validateFormAnswer(field, answers[field.id]);
    if (error) errors[field.id] = error;
  }
  return errors;
}
